import { LegalLayout, Section, Bullets } from "../components/LegalLayout";
import { Seo } from "../components/Seo";

export const PrivacyPolicy = () => (
  <LegalLayout
    title="Privacy Policy"
    updated="June 2026"
    intro="This Privacy Policy explains how Interlocked Solutions Ltd (“Interlocked Solutions”, “we”, “us” or “our”), an official UK channel partner for FireFlink, collects, uses and protects your personal data when you visit our website or request a demo. We are committed to handling your personal data in accordance with the UK General Data Protection Regulation (UK GDPR), the Data Protection Act 2018 and the Privacy and Electronic Communications Regulations (PECR)."
  >
    <Seo
      title="Privacy Policy | Interlocked Solutions (UK GDPR)"
      description="How Interlocked Solutions Ltd collects, uses and protects your personal data under UK GDPR, the Data Protection Act 2018 and PECR — including lawful bases, retention, your rights and how to contact us."
      path="/privacy-policy"
    />
    <Section heading="Who we are (Data Controller)">
      <p>
        Interlocked Solutions Ltd is the “data controller” responsible for your personal data
        collected through this website. If you have any questions about this policy or how we handle
        your data, contact us at{" "}
        <a href="mailto:info@interlockedsolutions.co.uk" className="text-primary underline underline-offset-2">
          info@interlockedsolutions.co.uk
        </a>
        .
      </p>
    </Section>

    <Section heading="Personal data we collect">
      <p>We only collect the data we need to respond to your enquiry and improve our services:</p>
      <Bullets
        items={[
          "Contact & enquiry data you submit via our demo/contact form: name, work email, company, phone number, your area of interest and any message you provide.",
          "Technical & usage data collected automatically via cookies and similar technologies: IP address, browser type, device information, pages visited and referring URLs (only where you have consented to analytics/marketing cookies).",
          "Communications data: records of correspondence when you contact us by email.",
        ]}
      />
      <p>
        We do not knowingly collect special category (sensitive) personal data, and we do not
        collect data from children.
      </p>
    </Section>

    <Section heading="Lawful bases for processing (UK GDPR Article 6)">
      <p>We rely on the following lawful bases:</p>
      <Bullets
        items={[
          "Consent — for placing non-essential (analytics and marketing) cookies and for sending you marketing communications. You can withdraw consent at any time.",
          "Legitimate interests — to respond to demo requests, operate and secure our website, and understand how it is used. We balance these interests against your rights and freedoms.",
          "Contract — where processing is necessary to take steps at your request before entering into a contract (e.g. providing a demo or quotation).",
          "Legal obligation — where we must process data to comply with the law.",
        ]}
      />
    </Section>

    <Section heading="How we use your data">
      <Bullets
        items={[
          "To respond to your demo request or enquiry and provide information about FireFlink and our services.",
          "To send you service and, where you have consented, marketing communications (you can opt out at any time).",
          "To operate, maintain, secure and improve our website.",
          "To comply with our legal and regulatory obligations.",
        ]}
      />
    </Section>

    <Section heading="Cookies">
      <p>
        We use cookies and similar technologies. Strictly necessary cookies are always active;
        analytics and marketing cookies are only set with your consent via our cookie banner. For
        full details and to change your preferences at any time, see our{" "}
        <a href="/cookie-policy" className="text-primary underline underline-offset-2">
          Cookie Policy
        </a>
        .
      </p>
    </Section>

    <Section heading="Who we share your data with">
      <p>We do not sell your personal data. We may share it with:</p>
      <Bullets
        items={[
          "FireFlink (our software partner) — where necessary to arrange a demo, licensing or support you have requested.",
          "Trusted service providers acting as our processors under written contracts — including our website/application hosting and email delivery providers — solely to provide services to us.",
          "Authorities or third parties where required by law, or to establish, exercise or defend legal claims.",
        ]}
      />
    </Section>

    <Section heading="International transfers">
      <p>
        Some of our service providers may process data outside the UK. Where personal data is
        transferred internationally, we ensure an adequate level of protection through appropriate
        safeguards, such as UK adequacy regulations or the UK International Data Transfer Agreement /
        addendum to the EU Standard Contractual Clauses.
      </p>
    </Section>

    <Section heading="How long we keep your data">
      <p>
        We keep your personal data only for as long as necessary for the purposes set out above. Enquiry
        and lead data is typically retained for up to 24 months from your last interaction, after which
        it is securely deleted or anonymised, unless we are required to keep it longer to meet legal
        obligations.
      </p>
    </Section>

    <Section heading="Your rights">
      <p>Under UK GDPR you have the right to:</p>
      <Bullets
        items={[
          "Be informed about how your data is used (this policy).",
          "Access a copy of your personal data.",
          "Rectify inaccurate or incomplete data.",
          "Erase your data (the “right to be forgotten”) in certain circumstances.",
          "Restrict or object to processing, including for direct marketing.",
          "Data portability.",
          "Withdraw consent at any time, without affecting processing carried out before withdrawal.",
        ]}
      />
      <p>
        To exercise any of these rights, email{" "}
        <a href="mailto:info@interlockedsolutions.co.uk" className="text-primary underline underline-offset-2">
          info@interlockedsolutions.co.uk
        </a>
        . We will respond within one month.
      </p>
    </Section>

    <Section heading="How to complain">
      <p>
        If you have concerns about how we handle your data, please contact us first. You also have the
        right to lodge a complaint with the UK Information Commissioner’s Office (ICO) at{" "}
        <a
          href="https://ico.org.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2"
        >
          ico.org.uk
        </a>{" "}
        or by calling 0303 123 1113.
      </p>
    </Section>

    <Section heading="Data security">
      <p>
        We use appropriate technical and organisational measures — including encryption in transit,
        access controls and secure hosting — to protect your personal data against unauthorised
        access, loss or misuse.
      </p>
    </Section>

    <Section heading="Changes to this policy">
      <p>
        We may update this policy from time to time. Any changes will be posted on this page with a
        revised “Last updated” date.
      </p>
    </Section>

    <Section heading="Contact us">
      <p>
        Interlocked Solutions Ltd, United Kingdom
        <br />
        Email:{" "}
        <a href="mailto:info@interlockedsolutions.co.uk" className="text-primary underline underline-offset-2">
          info@interlockedsolutions.co.uk
        </a>
      </p>
    </Section>
  </LegalLayout>
);
